import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  clearCompanyInvoiceDevice,
  createCompany,
  deleteCompany,
  getCompanies,
  getCompaniesExist,
  getDefaultCompany,
  getInvoicePrinters,
  updateCompany,
  type CompanyPayload,
  type InvoiceIssuingMode,
} from "../api/companies";
import { invoiceIssuingOf } from "../lib/fiscalInvoice";

// Под „devices“ — нов или изтрит принтер обновява и избора във „Фирми“
// (мутациите на устройствата инвалидират ["devices"] по префикс).
export const useInvoicePrinters = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ["devices", "invoice-printers"],
    queryFn: getInvoicePrinters,
    staleTime: 1000 * 60,
    enabled: options?.enabled ?? true,
  });
};

export const useCompanies = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ["companies"],
    queryFn: getCompanies,
    staleTime: 1000 * 60 * 30,
    enabled: options?.enabled ?? true,
  });
};

export const useDefaultCompany = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ["companies", "default"],
    queryFn: getDefaultCompany,
    // Винаги refetch при mount — InvoiceCreateModal зависи от
    // `last_invoice_number` за да изчисли „следващ номер +1". Без този
    // флаг последно използвания номер можеше да остане в кеша, ако
    // mutation invalidate не успее да обнови преди модалът да се отвори.
    staleTime: 0,
    refetchOnMount: "always",
    retry: false,
    enabled: options?.enabled ?? true,
  });
};

// „Къде се издават фактурите“ на текущата фирма — за входовете към софтуерна
// фактура извън „Фактуриране“ („Нов депозит“, „Справки“), които не я предлагат
// в `fiscal`. Същият кеш като `useDefaultCompany`, но без опресняване при всяко
// монтиране: режимът се сменя рядко, а BE отказва софтуерната фактура и сам.
// Без фирма (404) или без отговор — `software`, тоест екранът е както преди.
export const useDefaultCompanyInvoiceIssuing = (options?: {
  enabled?: boolean;
}): InvoiceIssuingMode => {
  const { data } = useQuery({
    queryKey: ["companies", "default"],
    queryFn: getDefaultCompany,
    staleTime: 1000 * 60,
    retry: false,
    enabled: options?.enabled ?? true,
  });
  return invoiceIssuingOf(data);
};

// Ключът е под „companies" — създаване/изтриване на фирма го обновява заедно
// със списъка.
export const useCompaniesExist = () => {
  return useQuery({
    queryKey: ["companies", "exists"],
    queryFn: getCompaniesExist,
    staleTime: 1000 * 60 * 30,
    retry: false,
  });
};

export const useCreateCompany = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CompanyPayload) => createCompany(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["companies"] });
    },
  });
};

// `releaseInvoiceDevice` — „Освободи устройството“, потвърдено във формата:
// отделният `DELETE` (`PUT` не маха закачането) тръгва ПРЕДИ записа, докато
// фирмата е още в записания си режим — в `fiscal` BE пази номера, който
// апаратът държи; при напускане на `fiscal` той е запазен вече, а
// освобождаване СЛЕД смяна към `fiscal` би запазило втори, излишен номер.
// Повторен запис след паднал `PUT` праща `DELETE` пак — без закачено
// устройство BE не прави нищо.
export const useUpdateCompany = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
      releaseInvoiceDevice = false,
    }: {
      id: number;
      data: CompanyPayload;
      releaseInvoiceDevice?: boolean;
    }) => {
      if (releaseInvoiceDevice) await clearCompanyInvoiceDevice(id);
      return updateCompany(id, data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["companies"] });
    },
    // Освободено, а записът е паднал — закачането в кеша вече не е вярно.
    onError: (_error, { releaseInvoiceDevice }) => {
      if (releaseInvoiceDevice) qc.invalidateQueries({ queryKey: ["companies"] });
    },
  });
};

export const useDeleteCompany = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteCompany(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["companies"] });
    },
  });
};

